import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { matchPath, withRouter, Link } from 'react-router-dom';
import AppMenu from '@afosto/components-react/AppMenu';
import AppDrawerHeader from '@afosto/components-react/AppDrawerHeader';
import AppMenuList from '@afosto/components-react/AppMenuList';
import AppMenuListSubheader from '@afosto/components-react/AppMenuListSubheader';
import AppMenuListItem from '@afosto/components-react/AppMenuListItem';
import Button from '@material-ui/core/Button';
import Popper from '@material-ui/core/Popper';
import Typography from '@material-ui/core/Typography';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useAppActions } from '../AppProvider';
import Divider from '@material-ui/core/Divider';
import Icon from '../Icon';

const DrawerMenu = ({ title, menu, primaryActions, primaryActionLabel, location }) => {
  const intl = useIntl();
  const anchorEl = useRef(null);
  const [actions] = useAppActions();
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  const handleCloseActionMenu = event => {
    if (anchorEl.current.contains(event.target)) {
      return;
    }

    setIsActionMenuOpen(false);
  };

  return (
    <AppMenu>
      <AppDrawerHeader>
        <Typography variant="h6" color="inherit">
          {title}
        </Typography>
      </AppDrawerHeader>
      {primaryActions.length === 1 && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<Icon icon={['fal', 'plus']} />}
          onClick={primaryActions[0]?.onClick}
        >
          {primaryActionLabel}
        </Button>
      )}
      {primaryActions.length > 1 && (
        <div style={{ position: 'relative' }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            buttonRef={anchorEl}
            aria-owns={isActionMenuOpen ? 'primary-action-menu' : undefined}
            aria-haspopup="true"
            onClick={() => setIsActionMenuOpen(!isActionMenuOpen)}
          >
            <Icon icon={['fal', 'plus']} style={{ marginRight: '16px' }} />
            {primaryActionLabel}
            <Icon icon={['fal', 'angle-down']} style={{ marginLeft: 'auto' }} />
          </Button>
          <Popper
            open={isActionMenuOpen}
            anchorEl={anchorEl.current}
            transition
            disablePortal
            style={{ zIndex: 5 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                id="primary-action-menu"
                style={{
                  transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom',
                }}
              >
                <Paper style={{ minWidth: 176 }}>
                  <ClickAwayListener onClickAway={handleCloseActionMenu}>
                    <MenuList>
                      {primaryActions.map(({ key, text, to, onClick }) =>
                        to ? (
                          <MenuItem
                            key={key}
                            onClick={handleCloseActionMenu}
                            component={Link}
                            to={to}
                          >
                            {text}
                          </MenuItem>
                        ) : (
                          <MenuItem
                            key={key}
                            onClick={event => {
                              if (onClick) {
                                onClick();
                              }

                              handleCloseActionMenu(event);
                            }}
                          >
                            {text}
                          </MenuItem>
                        ),
                      )}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      )}
      <AppMenuList>
        {menu.map(item => {
          if (item && item.key === 'divider') {
            return <Divider key={item.key} style={{ marginTop: 4, marginBottom: 4 }} />;
          }

          const getMenuItemLabel = ({ label, translationKey, external }) => (
            <>
              {translationKey
                ? intl.formatMessage({
                    id: translationKey,
                    defaultMessage: label,
                    description: `label of the main navigation item ${label}`,
                  })
                : label}
              {external && (
                <Icon
                  icon={['fal', 'external-link']}
                  fixedWidth
                  style={{ marginLeft: 16, marginBottom: -1 }}
                />
              )}
            </>
          );

          getMenuItemLabel.propTypes = {
            external: PropTypes.bool,
            label: PropTypes.string,
            translationKey: PropTypes.string,
          };

          getMenuItemLabel.defaultProps = {
            external: false,
            label: undefined,
            translationKey: undefined,
          };

          const renderMenuItem = menuItem => (
            <AppMenuListItem
              key={menuItem.key}
              button
              label={getMenuItemLabel(menuItem)}
              component={menuItem?.external ? 'a' : Link}
              {...(menuItem?.external
                ? { href: menuItem.to, target: '_blank', rel: 'noopener' }
                : { to: menuItem.to })}
              selected={
                !!matchPath(location.pathname, {
                  path: menuItem.to,
                  exact: menuItem.exact,
                  strict: false,
                })
              }
              onClick={() => actions.setIsDrawerOpen(false)}
            >
              {(menuItem.items || []).map(subMenuItem => renderMenuItem(subMenuItem))}
            </AppMenuListItem>
          );

          if (item && Array.isArray(item.items)) {
            return (
              <div key={item.key}>
                <AppMenuListSubheader disableGutters style={{ backgroundColor: '#fafafa' }}>
                  {getMenuItemLabel(item)}
                </AppMenuListSubheader>
                {item.items.map(menuItem => renderMenuItem(menuItem))}
              </div>
            );
          }

          return renderMenuItem(item);
        })}
      </AppMenuList>
    </AppMenu>
  );
};

DrawerMenu.defaultProps = {
  title: null,
  menu: [],
  primaryActions: [],
  primaryActionLabel: undefined,
  location: undefined,
};

DrawerMenu.propTypes = {
  title: PropTypes.string,
  menu: PropTypes.array,
  primaryActions: PropTypes.array,
  primaryActionLabel: PropTypes.string,
  location: PropTypes.object,
};

export default withRouter(DrawerMenu);
